/**
 * InboxTab — /phone v2's Inbox surface.
 *
 * Layout: TriageStrip (unnamed batch card) → filter chips + gold "New
 * text" primary → card grid (3 across, grouped by TODAY / YESTERDAY /
 * date) with pagination (NOT infinite scroll) → optional right-side
 * PersonTimeline slide-over when a card is clicked.
 *
 * Data: cross-channel per-phone rollup from useInboxRollup (client-side).
 * A true per-person timeline endpoint doesn't yet exist — see PART 3
 * ("Until that lands, assemble client-side by normalized phone match
 * and show a note when a channel can't be resolved").
 */
import { useEffect, useMemo, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import {
  MessageSquare, Phone, PhoneIncoming, PhoneOutgoing, PhoneMissed,
  Voicemail as VMIcon, CircleCheck, X as XIcon, ArrowLeft, ExternalLink,
  Building2, Trash2, Search, LayoutGrid, List, Send as SendIcon,
  Paperclip, ImageIcon, UserPlus, Pencil,
} from "lucide-react";
import { formatPhoneNumber, cn } from "../../lib/utils";
import { NAVY, ROYAL, GOLD, GREEN, RED, AMBER, NAVY_TINT, NAVY_HAIRLINE, statusAccent } from "./tokens";
import { useAuth } from "../../context/AuthContext";
import { smsAPI } from "../../services/api";
import { toast } from "sonner";
import { registerSlideover } from "../../lib/slideoverStore";
import MmsMedia from "../../components/comms/MmsMedia";
import EmojiQuickPicker from "../../components/comms/EmojiQuickPicker";
import QuickAddContactModal from "../../components/comms/QuickAddContactModal";
import { useContactSlideover } from "../../components/contacts/ContactDetailSlideover";

// Normalise a phone-ish string to +E.164 for outbound SMS. Kept local to
// avoid a new util file and to match the same helper used in
// Communications2.jsx (parent).
function e164Norm(raw) {
  const d = String(raw || "").replace(/\D/g, "");
  if (!d) return "";
  if (d.length === 10) return `+1${d}`;
  if (d.length === 11 && d.startsWith("1")) return `+${d}`;
  return `+${d}`;
}

const PAGE = 20;
const SMS_SEGMENT = 1600;          // Twilio's hard per-message cap
const SMS_MAX_CHARS = SMS_SEGMENT * 4;  // allow up to 4 auto-split messages
const smsSegments = (t) => Math.max(1, Math.ceil((t || "").length / SMS_SEGMENT));

// Two-letter initials for the thread avatar.
function initials(s) {
  const str = String(s || "").trim();
  if (!str) return "?";
  const parts = str.replace(/[+()\-]/g, "").split(/\s+/).filter(Boolean);
  if (parts.length >= 2 && /[A-Za-z]/.test(parts[0]) && /[A-Za-z]/.test(parts[1])) {
    return (parts[0][0] + parts[1][0]).toUpperCase();
  }
  const alpha = str.replace(/[^A-Za-z]/g, "");
  if (alpha.length >= 2) return alpha.slice(0, 2).toUpperCase();
  const digits = str.replace(/\D/g, "");
  return (digits.slice(-2) || str.slice(0, 2) || "?").toUpperCase();
}

// Compact timestamp for the list view — "12:34p" / "Mon" / "8/15".
function timeShort(ts) {
  if (!ts) return "—";
  const d = new Date(ts);
  if (Number.isNaN(d.getTime())) return "—";
  const now = new Date();
  const sameDay = d.toDateString() === now.toDateString();
  const dayMs = 86_400_000;
  if (sameDay) {
    return d.toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit" });
  }
  if (Date.now() - ts < 6 * dayMs) {
    return d.toLocaleDateString("en-US", { weekday: "short" });
  }
  return d.toLocaleDateString("en-US", { month: "numeric", day: "numeric" });
}

function fmtRel(ts) {
  if (!ts) return "";
  const then = new Date(ts);
  if (Number.isNaN(then.getTime())) return "";
  const diff = (Date.now() - then.getTime()) / 1000;
  if (diff < 45) return "now";
  if (diff < 3600) return `${Math.round(diff/60)}m`;
  if (diff < 86400) return `${Math.round(diff/3600)}h`;
  if (diff < 86400*7) return `${Math.round(diff/86400)}d`;
  return then.toLocaleDateString();
}

function dayLabel(ts) {
  if (!ts) return "";
  const t = new Date(ts); t.setHours(0,0,0,0);
  const today = new Date(); today.setHours(0,0,0,0);
  const yest = new Date(today); yest.setDate(yest.getDate() - 1);
  if (t.getTime() === today.getTime()) return "Today";
  if (t.getTime() === yest.getTime()) return "Yesterday";
  return new Date(ts).toLocaleDateString(undefined, { weekday: "long", month: "short", day: "numeric" });
}

/* ── Triage strip ────────────────────────────────────────────────
 * The ~412 numbers that have never been named must NOT fill the inbox.
 * Show ONE card: "<n> numbers have never been named · Mostly one-off
 * inbound calls" with "Review <n>" (batch naming, → /contacts?filter=unnamed)
 * and "Hide from inbox" (per-user in-memory hidden set for now).
 */
function TriageStrip({ unnamedCount, onReview, onHideAll }) {
  if (unnamedCount === 0) return null;
  return (
    <div
      className="flex items-center gap-3 rounded-lg px-3 py-2 mb-3"
      style={{ background: "#F8FAFC", borderLeft: `3px solid ${AMBER}` }}
      data-testid="comms2-triage-strip"
    >
      <div className="flex-1 min-w-0">
        <span className="text-[12.5px] font-medium" style={{ color: NAVY }}>
          {unnamedCount} number{unnamedCount === 1 ? "" : "s"} have never been named
        </span>
        <span className="text-[11.5px] text-slate-500 ml-2 hidden md:inline">Mostly one-off inbound calls</span>
      </div>
      <div className="flex items-center gap-2 shrink-0">
        <button
          onClick={onReview}
          className="h-8 px-3 rounded-md text-white text-[12px] font-semibold hover:brightness-95 whitespace-nowrap"
          style={{ background: NAVY }}
          data-testid="comms2-triage-review"
        >
          Review {unnamedCount}
        </button>
        <button
          onClick={onHideAll}
          className="h-8 px-3 rounded-md border text-[12px] font-medium hover:bg-white whitespace-nowrap"
          style={{ borderColor: NAVY_HAIRLINE, color: NAVY }}
          data-testid="comms2-triage-hide-all"
        >
          Hide from inbox
        </button>
      </div>
    </div>
  );
}

/* ── Filter chips ──────────────────────────────────────────────── */
function FilterChips({ filter, setFilter, scroll = false }) {
  // Iter 508j — Calls and Texts moved to first-class top-nav tabs, so
  // they no longer appear as Inbox filter chips (would be redundant).
  // Inbox is now purely the work-queue: which items need action, and
  // of what shape (missed / VM / any).
  const chips = [
    { k: "needs", label: "Needs a response" },
    { k: "missed", label: "Missed" },
    { k: "vm",    label: "Voicemail" },
    { k: "all",   label: "All" },
  ];
  return (
    <div
      className={cn(
        "flex items-center gap-2",
        scroll ? "flex-nowrap overflow-x-auto [&::-webkit-scrollbar]:hidden" : "flex-wrap"
      )}
      style={scroll ? { scrollbarWidth: "none" } : undefined}
      data-testid="comms2-inbox-filters"
    >
      {chips.map(c => (
        <button
          key={c.k}
          onClick={() => setFilter(c.k)}
          className={cn(
            "px-3 h-8 rounded-full text-[12px] font-medium transition-colors shrink-0 whitespace-nowrap",
            filter === c.k ? "text-white" : "hover:bg-slate-100"
          )}
          style={filter === c.k ? { background: NAVY } : { color: NAVY, background: "transparent", border: `1px solid ${NAVY_HAIRLINE}` }}
          data-testid={`comms2-filter-${c.k}`}
        >
          {c.label}
        </button>
      ))}
    </div>
  );
}

/* ── Card ──────────────────────────────────────────────────────── */
function InboxCard({ entry, onOpen, onCall, onText, onPlayVm, onMarkHandled, onHide, onDelete, onAddContact, onEditContact, showCompanyBadge }) {
  const accent = statusAccent(entry);
  const contact = entry.contact;
  const chartId = contact?.patient_id;
  const contextBits = [];
  if (entry.contactType) contextBits.push(entry.contactType);
  if (contact?.mco) contextBits.push(contact.mco);
  const kindIcon =
    entry.latestKind === "sms" ? <MessageSquare className="w-3.5 h-3.5" style={{ color: ROYAL }} />
    : entry.latestKind === "vm" ? <VMIcon className="w-3.5 h-3.5" style={{ color: ROYAL }} />
    : entry.latestKind === "call" ? (
        entry.latestMissed ? <PhoneMissed className="w-3.5 h-3.5" style={{ color: RED }} />
        : entry.latestDirection === "inbound" ? <PhoneIncoming className="w-3.5 h-3.5" style={{ color: NAVY }} />
        : <PhoneOutgoing className="w-3.5 h-3.5" style={{ color: NAVY }} />
      )
    : null;

  return (
    <div
      role="button"
      tabIndex={0}
      onClick={() => onOpen(entry)}
      onKeyDown={(e) => { if (e.key === "Enter") onOpen(entry); }}
      className="group rounded-xl bg-white border p-3 hover:shadow-md transition-shadow cursor-pointer"
      style={{ borderColor: NAVY_HAIRLINE, borderTop: `3px solid ${accent === "transparent" ? NAVY_HAIRLINE : accent}` }}
      data-testid={`comms2-card-${entry.key}`}
    >
      {/* Row 1 — name + timestamp */}
      <div className="flex items-start gap-2">
        <div className="flex-1 min-w-0">
          <div className="text-[13px] font-semibold truncate flex items-center gap-1.5" style={{ color: NAVY }}>
            <span className="truncate">{entry.displayName}</span>
            {entry.needsRename && (
              <span className="text-[9px] px-1 py-px rounded uppercase font-bold" style={{ background: `${AMBER}22`, color: AMBER }}>needs name</span>
            )}
            {/* Company badge — surfaced to super_admin so they always
                know which tenant an inbox row belongs to (Dawn's ask,
                iter 475). Non-super-admins are already company-scoped
                by the backend so the badge would be a constant, no-
                signal repeat — hidden for them. */}
            {showCompanyBadge && entry.companyName && (
              <span
                className="inline-flex items-center gap-1 text-[9.5px] px-1.5 py-px rounded font-semibold uppercase tracking-wide shrink-0"
                style={{ background: `${ROYAL}18`, color: ROYAL, borderRadius: 4 }}
                title={`Tenant: ${entry.companyName}`}
                data-testid={`comms2-card-company-${entry.key}`}
              >
                <Building2 className="w-2.5 h-2.5" />
                {entry.companyName}
              </span>
            )}
          </div>
        </div>
        <div className="text-[10px] text-slate-400 tabular-nums shrink-0">{fmtRel(entry.latestTs)}</div>
      </div>
      {/* Row 2 — context */}
      {contextBits.length > 0 && (
        <div className="text-[11px] text-slate-600 mt-0.5 truncate">{contextBits.join(" · ")}</div>
      )}
      {/* Row 3 — activity + channel icon */}
      <div className="flex items-center gap-1.5 text-[12px] text-slate-700 mt-2 truncate">
        {kindIcon}
        <span className="truncate">{entry.latestSummary || "—"}</span>
      </div>
      {/* Row 4 — actions */}
      <div className="flex items-center gap-1.5 mt-2.5 pt-2 border-t" style={{ borderColor: NAVY_HAIRLINE }} onClick={(e) => e.stopPropagation()}>
        {/* Primary — call (green) */}
        <button
          onClick={() => onCall(entry)}
          className="h-8 px-2.5 rounded-md text-white text-[11.5px] font-semibold flex items-center gap-1 hover:brightness-95"
          style={{ background: GREEN }}
          data-testid={`comms2-card-call-${entry.key}`}
          title="Call"
        >
          <Phone className="w-3.5 h-3.5" /> Call
        </button>
        {/* Secondary — text (outlined) */}
        <button
          onClick={() => onText(entry)}
          className="h-8 px-2.5 rounded-md border text-[11.5px] font-medium flex items-center gap-1 hover:bg-slate-50"
          style={{ borderColor: NAVY_HAIRLINE, color: NAVY }}
          data-testid={`comms2-card-text-${entry.key}`}
          title="Text"
        >
          <MessageSquare className="w-3.5 h-3.5" /> Text
        </button>
        {/* VM playback — only shown when there's a voicemail on the entry */}
        {entry.voicemails.length > 0 && (
          <button
            onClick={() => onPlayVm(entry)}
            className="h-8 px-2.5 rounded-md text-white text-[11.5px] font-semibold flex items-center gap-1 hover:brightness-95"
            style={{ background: GREEN }}
            data-testid={`comms2-card-play-${entry.key}`}
            title="Play voicemail"
          >
            <VMIcon className="w-3.5 h-3.5" /> Play
          </button>
        )}
        <div className="ml-auto flex items-center gap-1">
          {/* Mark handled */}
          {(entry.needsCallback || entry.unheardVm || entry.unreadSms) && (
            <button
              onClick={() => onMarkHandled(entry)}
              className="h-8 w-8 rounded-md hover:bg-slate-100 flex items-center justify-center"
              title="Mark handled"
              data-testid={`comms2-card-handled-${entry.key}`}
            >
              <CircleCheck className="w-4 h-4" style={{ color: GREEN }} />
            </button>
          )}
          {/* Hide (only for unnamed numbers so real contacts aren't silenced by accident) */}
          {entry.needsRename && typeof onAddContact === "function" && (
            <button
              onClick={(e) => { e.stopPropagation(); onAddContact(entry); }}
              className="h-8 w-8 rounded-md hover:bg-emerald-50 flex items-center justify-center"
              title="Add as contact"
              data-testid={`comms2-card-add-contact-${entry.key}`}
            >
              <UserPlus className="w-4 h-4 text-emerald-600" />
            </button>
          )}
          {/* Iter 505 — Edit affordance for named contacts. Same rules
              as the row layout: hides on unnamed entries (Add-Contact
              covers that case) so a single row never shows both. */}
          {!entry.needsRename && entry.contact?.id && typeof onEditContact === "function" && (
            <button
              onClick={(e) => { e.stopPropagation(); onEditContact(entry.contact.id); }}
              className="h-8 w-8 rounded-md hover:bg-slate-100 flex items-center justify-center"
              title="Edit contact"
              data-testid={`comms2-card-edit-contact-${entry.key}`}
            >
              <Pencil className="w-4 h-4 text-slate-500" />
            </button>
          )}
          {entry.needsRename && (
            <button
              onClick={() => onHide(entry.key)}
              className="h-8 w-8 rounded-md hover:bg-slate-100 flex items-center justify-center"
              title="Hide from inbox"
              data-testid={`comms2-card-hide-${entry.key}`}
            >
              <XIcon className="w-4 h-4 text-slate-500" />
            </button>
          )}
          {/* Iter 484 — Delete the whole SMS thread (server-side).
              Only offered when there's actually a text thread to
              delete + `onDelete` is wired. Hard-deletes on the
              server, so we confirm first. */}
          {entry.smsThread && typeof onDelete === "function" && (
            <button
              onClick={(e) => {
                e.stopPropagation();
                const name = entry.displayName || formatPhoneNumber(entry.phone);
                if (window.confirm(`Delete the entire text thread with ${name}?\n\nThis cannot be undone.`)) {
                  onDelete(entry);
                }
              }}
              className="h-8 w-8 rounded-md hover:bg-red-50 flex items-center justify-center"
              title="Delete this text thread"
              data-testid={`comms2-card-delete-${entry.key}`}
            >
              <Trash2 className="w-4 h-4" style={{ color: RED }} />
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

/* ── List-view row (iter 485) — single-line compact layout. Same
 *    entry, same actions, smaller footprint for power users. */
function InboxRow({ entry, onOpen, onCall, onText, onPlayVm, onMarkHandled, onHide, onDelete, onAddContact, onEditContact, showCompanyBadge, isLast }) {
  const has = {
    call: entry.calls.length > 0,
    vm: entry.voicemails.length > 0,
    sms: !!entry.smsThread,
  };
  const accent = statusAccent(entry);
  return (
    <div
      className="flex items-center gap-3 px-3 py-2 hover:bg-slate-50 cursor-pointer transition"
      style={{ borderBottom: isLast ? "none" : `1px solid ${NAVY_HAIRLINE}` }}
      onClick={() => onOpen(entry)}
      data-testid={`comms2-row-${entry.key}`}
    >
      {/* Accent stripe (indicates urgency) */}
      <div className="w-1 self-stretch rounded-full" style={{ background: accent }} />

      {/* Name + phone */}
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          <span className="text-[13px] font-semibold truncate" style={{ color: NAVY }}>
            {entry.displayName}
          </span>
          {entry.contactType && (
            <span className="text-[9px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded" style={{ background: NAVY_TINT, color: NAVY }}>
              {entry.contactType}
            </span>
          )}
          {showCompanyBadge && entry.companyName && (
            <span className="text-[9px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded flex items-center gap-0.5 whitespace-nowrap shrink-0 max-w-[110px]" style={{ background: NAVY_TINT, color: NAVY }}>
              <Building2 className="w-2.5 h-2.5 shrink-0" />
              <span className="truncate">{entry.companyName}</span>
            </span>
          )}
          {entry.needsCallback && (
            <span className="text-[9px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded" style={{ background: "#FEF2F2", color: RED }}>
              Missed
            </span>
          )}
          {(entry.unheardVm || entry.unreadSms) && (
            <span className="text-[9px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded" style={{ background: "#FFFBEB", color: AMBER }}>
              Unread
            </span>
          )}
        </div>
        <div className="text-[11px] text-slate-500 truncate mt-0.5">
          <span className="font-mono">{formatPhoneNumber(entry.phone)}</span>
          {entry.latestSummary && <> · {entry.latestSummary}</>}
        </div>
      </div>

      {/* Channel dots */}
      <div className="flex items-center gap-1 shrink-0 text-slate-400">
        {has.call && <Phone className="w-3.5 h-3.5" />}
        {has.vm && <VMIcon className="w-3.5 h-3.5" />}
        {has.sms && <MessageSquare className="w-3.5 h-3.5" />}
      </div>

      {/* Timestamp */}
      <div className="text-[11px] text-slate-500 tabular-nums whitespace-nowrap shrink-0 min-w-[68px] text-right">
        {timeShort(entry.latestTs)}
      </div>

      {/* Actions */}
      <div className="flex items-center gap-0.5 shrink-0" onClick={(e) => e.stopPropagation()}>
        {has.sms && (
          <button
            onClick={() => onText(entry)}
            className="h-7 w-7 rounded hover:bg-slate-100 flex items-center justify-center"
            title="Text"
            data-testid={`comms2-row-text-${entry.key}`}
          >
            <MessageSquare className="w-3.5 h-3.5" style={{ color: ROYAL }} />
          </button>
        )}
        <button
          onClick={() => onCall(entry)}
          className="h-7 w-7 rounded hover:bg-slate-100 flex items-center justify-center"
          title="Call"
          data-testid={`comms2-row-call-${entry.key}`}
        >
          <Phone className="w-3.5 h-3.5" style={{ color: GREEN }} />
        </button>
        {has.vm && (
          <button
            onClick={() => onPlayVm(entry.voicemails[0])}
            className="h-7 w-7 rounded hover:bg-slate-100 flex items-center justify-center"
            title="Play voicemail"
            data-testid={`comms2-row-vm-${entry.key}`}
          >
            <VMIcon className="w-3.5 h-3.5" style={{ color: AMBER }} />
          </button>
        )}
        {entry.needsCallback && (
          <button
            onClick={() => onMarkHandled(entry)}
            className="h-7 w-7 rounded hover:bg-slate-100 flex items-center justify-center"
            title="Mark handled"
            data-testid={`comms2-row-handled-${entry.key}`}
          >
            <CircleCheck className="w-3.5 h-3.5" style={{ color: GREEN }} />
          </button>
        )}
        {entry.needsRename && typeof onAddContact === "function" && (
          <button
            onClick={() => onAddContact(entry)}
            className="h-7 w-7 rounded hover:bg-emerald-50 flex items-center justify-center"
            title="Add as contact"
            data-testid={`comms2-row-add-contact-${entry.key}`}
          >
            <UserPlus className="w-3.5 h-3.5" style={{ color: "#059669" }} />
          </button>
        )}
        {/* Iter 505 — named contacts get a compact Edit affordance so
            operators can fix a wrong number or affiliation right from
            the inbox without leaving to /contacts and hunting. */}
        {!entry.needsRename && entry.contact?.id && typeof onEditContact === "function" && (
          <button
            onClick={() => onEditContact(entry.contact.id)}
            className="h-7 w-7 rounded hover:bg-slate-100 flex items-center justify-center"
            title="Edit contact"
            data-testid={`comms2-row-edit-contact-${entry.key}`}
          >
            <Pencil className="w-3.5 h-3.5 text-slate-500" />
          </button>
        )}
        {has.sms && typeof onDelete === "function" && (
          <button
            onClick={() => {
              const name = entry.displayName || formatPhoneNumber(entry.phone);
              if (window.confirm(`Delete the entire text thread with ${name}?\n\nThis cannot be undone.`)) {
                onDelete(entry);
              }
            }}
            className="h-7 w-7 rounded hover:bg-red-50 flex items-center justify-center"
            title="Delete text thread"
            data-testid={`comms2-row-delete-${entry.key}`}
          >
            <Trash2 className="w-3.5 h-3.5" style={{ color: RED }} />
          </button>
        )}
      </div>
    </div>
  );
}

/* ── Cross-channel timeline panel ─────────────────────────────── */
function TimelinePanel({ entry, onClose, onCall, navigate, fromNumber, onInboxChanged, openContactSlideover }) {
  const [household, setHousehold] = useState(null);
  const [householdGap, setHouseholdGap] = useState("");
  // Iter 487 — actually load the SMS history for this phone. Before this
  // fix the panel just showed "Text thread · latest: ..." one-liner and
  // the real back-and-forth was invisible.
  const [smsMessages, setSmsMessages] = useState([]);
  const [smsLoading, setSmsLoading] = useState(false);
  // Iter 488 — inline SMS composer. Reply from the panel instead of
  // switching over to the Texts tab.
  const [draft, setDraft] = useState("");
  const [sending, setSending] = useState(false);
  // Iter 494 — attach image/file + emoji picker in the inline composer.
  const [attachment, setAttachment] = useState(null);
  const [dragOver, setDragOver] = useState(false);
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef(null);
  const textareaRef = useRef(null);

  // Auto-grow the reply box as the user types (roomy default, caps then scrolls).
  useEffect(() => {
    const el = textareaRef.current;
    if (!el) return;
    el.style.height = "auto";
    el.style.height = `${Math.min(el.scrollHeight, 240)}px`;
  }, [draft]);

  // Iter 490 — announce that a right-side slide-over is open so the
  // global Ask Avi FAB auto-hides while the composer is visible.
  useEffect(() => {
    const off = registerSlideover();
    return () => off();
  }, []);

  // Load household members whenever the panel opens for a new phone.
  // Excludes the entry's own primary contact record so the list only
  // shows OTHER people who share the landline (per PART 3: "in home
  // care a client and her daughter often share a landline and whoever
  // answers needs to know").
  useEffect(() => {
    if (!entry) return;
    let alive = true;
    (async () => {
      setHousehold(null); setHouseholdGap("");
      try {
        const res = await fetch(
          `${process.env.REACT_APP_BACKEND_URL}/api/contacts/household/${encodeURIComponent(entry.phone)}`,
          { headers: { Authorization: `Bearer ${localStorage.getItem("token") || ""}` } },
        );
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const data = await res.json();
        // Deduplicate + drop the primary contact whose id matches the
        // one already rendered in the "Type/Status" block above.
        const own = entry.contact?.id;
        const others = (data.members || []).filter(m => m.id !== own);
        if (alive) setHousehold(others);
      } catch {
        if (alive) setHouseholdGap("Household lookup unavailable");
      }
    })();
    return () => { alive = false; };
  }, [entry]);

  // Pull the full SMS conversation whenever the panel opens for a
  // new phone. Poll every 10s so incoming replies land without a
  // manual refresh; skip while the tab is hidden.
  useEffect(() => {
    if (!entry?.phone) { setSmsMessages([]); return; }
    let alive = true;
    const load = async () => {
      try {
        const our = entry.smsThread?.our_number || entry.ourNumber || null;
        setSmsLoading(true);
        const r = await smsAPI.getConversation(entry.phone, our, 200);
        if (!alive) return;
        // Backends return either a list or {messages: [...]} depending on
        // rev — normalise so the render doesn't have to care.
        const raw = r.data;
        const list = Array.isArray(raw) ? raw : (raw?.messages || []);
        setSmsMessages(list);
      } catch {
        if (alive) setSmsMessages([]);
      } finally {
        if (alive) setSmsLoading(false);
      }
    };
    load();
    const t = setInterval(() => {
      if (document.visibilityState === "visible") load();
    }, 10000);
    return () => { alive = false; clearInterval(t); };
  }, [entry?.phone, entry?.smsThread?.our_number, entry?.ourNumber]);

  if (!entry) return null;
  const items = [];
  for (const c of entry.calls) items.push({ kind: "call", ts: c.created_at, data: c });
  for (const v of entry.voicemails) items.push({ kind: "vm", ts: v.created_at, data: v });
  for (const m of smsMessages) {
    items.push({ kind: "sms", ts: m.created_at || m.timestamp, data: m });
  }
  items.sort((a, b) => new Date(a.ts || 0) - new Date(b.ts || 0));

  // Iter 488 — inline reply. Normalises the recipient to +E.164 and uses
  // the current line as the outbound number (falling back to the thread's
  // recorded our_number so replying to an older thread doesn't silently
  // switch which company the text comes from).
  const sendReply = async () => {
    const body = draft.trim();
    if ((!body && !attachment) || sending) return;
    const to = e164Norm(entry.phone);
    if (!to) { toast.error("This entry has no valid phone number to reply to."); return; }
    const from = entry.smsThread?.our_number || entry.ourNumber || fromNumber || "";
    if (!from) { toast.error("Pick an outbound line at the top of the page first."); return; }
    setSending(true);
    // Optimistic bubble so it feels instant.
    const optimistic = {
      id: `local-${Date.now()}`,
      direction: "outbound",
      is_outbound: true,
      message_body: body,
      created_at: new Date().toISOString(),
      status: "sending",
      // Local-only preview URL so the user sees their attachment
      // immediately; replaced by the real row on the refetch.
      _optimistic_attachment_name: attachment?.name || null,
    };
    setSmsMessages(prev => [...prev, optimistic]);
    const stashedAttachment = attachment;
    setDraft("");
    setAttachment(null);
    if (fileInputRef.current) fileInputRef.current.value = "";
    try {
      // Upload the attachment first if present. Uses the same
      // smsAPI.uploadMedia endpoint that ReceptionistSMSPanel calls;
      // returns a publicly-fetchable URL that Twilio can pick up.
      let mediaUrls;
      if (stashedAttachment) {
        setUploading(true);
        const fd = new FormData();
        fd.append("file", stashedAttachment);
        const up = await smsAPI.uploadMedia(fd);
        mediaUrls = [up.data.url];
        setUploading(false);
      }
      await smsAPI.send({ to, from, body, media_urls: mediaUrls });
      toast.success(mediaUrls ? "MMS sent" : "Message sent");
      // Real message replaces the optimistic bubble on the next fetch.
      try {
        const r = await smsAPI.getConversation(entry.phone, from, 200);
        const raw = r.data;
        const list = Array.isArray(raw) ? raw : (raw?.messages || []);
        setSmsMessages(list);
      } catch { /* keep optimistic */ }
      onInboxChanged?.();
    } catch (e) {
      // Rollback optimistic bubble + restore draft so the user can retry.
      setSmsMessages(prev => prev.filter(m => m.id !== optimistic.id));
      setDraft(body);
      setAttachment(stashedAttachment);
      const msg = e?.response?.data?.detail || e?.message || "Send failed";
      toast.error(String(msg));
    } finally {
      setSending(false);
      setUploading(false);
    }
  };

  const onEmojiPick = (char) => {
    // Insert at caret if we can, else append. Keeps typing flow natural.
    const el = textareaRef.current;
    if (!el) { setDraft(d => (d + char).slice(0, SMS_MAX_CHARS)); return; }
    const start = el.selectionStart ?? draft.length;
    const end = el.selectionEnd ?? draft.length;
    const next = (draft.slice(0, start) + char + draft.slice(end)).slice(0, SMS_MAX_CHARS);
    setDraft(next);
    // Restore caret position after React re-renders.
    requestAnimationFrame(() => {
      el.focus();
      const pos = Math.min(next.length, start + char.length);
      try { el.setSelectionRange(pos, pos); } catch { /* noop */ }
    });
  };

  const acceptFile = (f) => {
    if (!f) return;
    // Guard against oversized files early — the backend caps at 5MB.
    if (f.size > 5 * 1024 * 1024) {
      toast.error("Attachment must be under 5MB");
      if (fileInputRef.current) fileInputRef.current.value = "";
      return;
    }
    setAttachment(f);
  };

  const onPickFile = (e) => acceptFile(e.target.files?.[0]);

  const onDropFile = (e) => {
    e.preventDefault();
    setDragOver(false);
    const f = e.dataTransfer?.files?.[0];
    if (f) acceptFile(f);
  };

  return (
    <div
      className="w-full h-full bg-white flex flex-col min-w-0"
      data-testid="comms2-timeline-panel"
      onDragOver={(e) => { e.preventDefault(); if (!dragOver) setDragOver(true); }}
      onDragLeave={(e) => { if (e.currentTarget === e.target) setDragOver(false); }}
      onDrop={onDropFile}
    >
      {dragOver && (
        <div
          className="absolute inset-0 z-[80] bg-[#eef4ff]/90 border-2 border-dashed rounded-lg flex flex-col items-center justify-center pointer-events-none"
          style={{ borderColor: NAVY }}
          data-testid="comms2-timeline-dropzone"
        >
          <Paperclip className="w-10 h-10 mb-2" style={{ color: NAVY }} />
          <p className="text-sm font-semibold" style={{ color: NAVY }}>Drop image to attach</p>
          <p className="text-xs text-slate-500">Max 5MB · sends as MMS</p>
        </div>
      )}
      <div className="shrink-0 px-4 h-16 border-b flex items-center gap-3" style={{ borderColor: NAVY_HAIRLINE }}>
        <button onClick={onClose} className="h-9 w-9 rounded-lg hover:bg-slate-100 flex items-center justify-center shrink-0" data-testid="comms2-timeline-close" title="Back to list">
          <ArrowLeft className="w-5 h-5" style={{ color: NAVY }} />
        </button>
        <div className="h-11 w-11 rounded-full flex items-center justify-center shrink-0 text-[15px] font-bold" style={{ background: `${NAVY}14`, color: NAVY }}>
          {initials(entry.displayName || entry.phone)}
        </div>
        <div className="flex-1 min-w-0">
          {/* Iter 508k · Click-to-open contract — clicking the person
              name in the thread header opens the canonical
              ContactDetailSlideover. Falls back to phone lookup if
              the entry has no contact_id (unknown numbers). */}
          <button
            type="button"
            onClick={() => {
              if (openContactSlideover) {
                openContactSlideover({
                  contactId: entry.contact?.id,
                  phone: entry.phone,
                });
              }
            }}
            className="text-[17px] font-semibold truncate hover:underline text-left block max-w-full"
            style={{ color: NAVY }}
            data-testid="comms2-timeline-open-contact"
          >
            {entry.displayName}
          </button>
          <div className="text-[12px] font-mono text-slate-500">{formatPhoneNumber(entry.phone)}</div>
        </div>
        <button
          onClick={() => onCall && onCall(entry)}
          className="h-10 px-4 rounded-lg text-white text-[13px] font-semibold flex items-center gap-1.5 hover:brightness-95 active:scale-[0.98] transition shrink-0"
          style={{ background: GREEN }}
          data-testid="comms2-timeline-call"
          title="Call this number"
        >
          <Phone className="w-4 h-4" /> <span className="hidden sm:inline">Call</span>
        </button>
      </div>
      {/* Right-side context block (per PART 3) */}
      <div className="shrink-0 border-b bg-slate-50 px-4 py-3 space-y-1 text-[12px]" style={{ borderColor: NAVY_HAIRLINE }}>
        {entry.contact ? (
          <>
            {entry.contact.contact_type && <div><span className="text-slate-500">Type:</span> <span className="font-medium">{entry.contact.contact_type}</span></div>}
            {entry.contact.status && <div><span className="text-slate-500">Status:</span> <span className="font-medium">{entry.contact.status}</span></div>}
            {entry.contact.patient_id && (
              <button onClick={() => navigate(`/healthcare/patients/${entry.contact.patient_id}`)} className="text-[12px] font-medium hover:underline flex items-center gap-1" style={{ color: ROYAL }}>
                <ExternalLink className="w-3 h-3" /> Open chart
              </button>
            )}
            {entry.contact.mco && <div><span className="text-slate-500">MCO:</span> <span className="font-medium">{entry.contact.mco}</span></div>}
            {entry.contact.assigned_pcw && <div><span className="text-slate-500">PCW:</span> <span className="font-medium">{entry.contact.assigned_pcw}</span></div>}
            {entry.contact.auth_expires_on && <div><span className="text-slate-500">Auth expires:</span> <span className="font-medium">{entry.contact.auth_expires_on}</span></div>}
          </>
        ) : (
          <div className="text-amber-700 italic">Not in contact directory yet</div>
        )}
        {/* Also reachable — household members on the same landline. */}
        <div className="pt-1.5 border-t mt-1.5" style={{ borderColor: NAVY_HAIRLINE }} data-testid="comms2-timeline-household">
          <div className="text-[10.5px] uppercase tracking-wider font-semibold text-slate-500 mb-0.5">Also reachable</div>
          {household === null && !householdGap && (
            <div className="text-[11.5px] text-slate-400 italic">Looking up household…</div>
          )}
          {householdGap && (
            <div className="text-[11.5px] text-slate-500 italic">{householdGap}</div>
          )}
          {household && household.length === 0 && !householdGap && (
            <div className="text-[11.5px] text-slate-400 italic">Nobody else on this line</div>
          )}
          {household && household.length > 0 && (
            <div className="space-y-1">
              {household.map(m => (
                <button
                  key={`${m.source}-${m.id}`}
                  onClick={() => m.source === "patient" ? navigate(`/healthcare/patients/${m.id}`) : navigate(`/contacts/${m.id}`)}
                  className="w-full flex items-center justify-between hover:bg-white/60 rounded px-1.5 py-1 -mx-1.5"
                  data-testid={`comms2-timeline-household-${m.source}-${m.id}`}
                >
                  <span className="text-[12px] font-medium truncate" style={{ color: NAVY }}>
                    {m.first_name} {m.last_name}
                  </span>
                  <span className="text-[10px] text-slate-500 ml-2 shrink-0 flex items-center gap-1">
                    {m.relationship_to_household && <span className="italic">{m.relationship_to_household}</span>}
                    <span>· {m.source === "patient" ? m.status || "chart" : m.contact_type || "contact"}</span>
                  </span>
                </button>
              ))}
            </div>
          )}
        </div>
      </div>
      {/* Timeline body — cross-channel chronological view. Calls,
          voicemails, and every SMS message render as their own row
          (SMS is bubble-styled inbound-left / outbound-right so the
          history reads like a real conversation). */}
      <div className="flex-1 overflow-y-auto p-5 md:p-6 space-y-3 text-[14px]" data-testid="comms2-timeline-body">
        {items.length === 0 && !smsLoading && (
          <div className="text-slate-400 italic" data-testid="comms2-timeline-empty">No activity yet</div>
        )}
        {smsLoading && smsMessages.length === 0 && (
          <div className="text-slate-400 italic" data-testid="comms2-timeline-loading">Loading conversation…</div>
        )}
        {items.map((it, i) => {
          if (it.kind === "sms") {
            const m = it.data;
            const isOut = m.direction === "outbound" || m.is_outbound;
            const body = m.message_body || m.body || m.message || m.text || "";
            const media = Array.isArray(m.media_urls) ? m.media_urls : [];
            const hasMedia = media.length > 0;
            // An in-flight optimistic bubble with an attachment name is
            // treated as "has media" for display purposes even though the
            // upload URL isn't back yet — prevents a jarring "(empty)"
            // flash while the file is uploading.
            const hasPendingAttachment = !!m._optimistic_attachment_name;
            // Prefer the row id; some inbound copies may only carry a
            // message_sid — the proxy accepts either.
            const mediaKey = m.id || m.message_sid || m.sid || "";
            return (
              <div
                key={`sms-${m.id || m.sid || i}`}
                className={cn("flex", isOut ? "justify-end" : "justify-start")}
                data-testid={`comms2-timeline-sms-${isOut ? "out" : "in"}`}
              >
                <div
                  className={cn(
                    "max-w-[72%] rounded-2xl px-4 py-2.5 shadow-sm",
                    isOut ? "text-white rounded-br-sm" : "bg-white border rounded-bl-sm",
                  )}
                  style={isOut
                    ? { background: NAVY }
                    : { borderColor: NAVY_HAIRLINE, color: NAVY }}
                >
                  {hasMedia && (
                    <div className="space-y-1.5 mb-1.5" data-testid="comms2-timeline-sms-media">
                      {media.map((url, mi) => (
                        <MmsMedia
                          key={`${mediaKey}-${mi}`}
                          messageId={mediaKey}
                          index={mi}
                          rawUrl={url}
                          outbound={isOut}
                        />
                      ))}
                    </div>
                  )}
                  {hasPendingAttachment && !hasMedia && (
                    <div className={cn(
                      "flex items-center gap-1.5 text-[11px] italic mb-1",
                      isOut ? "text-white/80" : "text-slate-500",
                    )} data-testid="comms2-timeline-sms-pending-attachment">
                      <Paperclip className="w-3 h-3" />
                      Uploading {m._optimistic_attachment_name}…
                    </div>
                  )}
                  {body ? (
                    <div className="whitespace-pre-wrap break-words text-[15px]">{body}</div>
                  ) : !hasMedia && !hasPendingAttachment ? (
                    <div className="whitespace-pre-wrap break-words text-[15px]">
                      <span className="italic opacity-60">(empty)</span>
                    </div>
                  ) : null}
                  <div className={cn("text-[11.5px] mt-1 flex items-center gap-1", isOut ? "text-white/70 justify-end" : "text-slate-500")}>
                    <span>{it.ts ? new Date(it.ts).toLocaleString() : ""}</span>
                    {m.status && <span className="uppercase tracking-wide">· {m.status}</span>}
                  </div>
                </div>
              </div>
            );
          }
          return (
            <div key={`x-${i}`} className="rounded-md border p-2.5" style={{ borderColor: NAVY_HAIRLINE }}>
              <div className="flex items-center gap-2 text-[11px] text-slate-500 mb-0.5">
                {it.kind === "call" && (it.data.direction === "inbound" ? <PhoneIncoming className="w-3 h-3" /> : <PhoneOutgoing className="w-3 h-3" />)}
                {it.kind === "vm" && <VMIcon className="w-3 h-3" />}
                <span>{it.ts ? new Date(it.ts).toLocaleString() : "unknown time"}</span>
              </div>
              {it.kind === "call" && <div>{it.data.direction === "inbound" ? "Inbound call" : "Outbound call"} · {it.data.status || "unknown"}</div>}
              {it.kind === "vm" && <div>Voicemail{it.data.transcription ? ` — "${it.data.transcription.slice(0, 200)}"` : ""}</div>}
            </div>
          );
        })}
        {/* Fax and notes channels don't (yet) have a per-phone endpoint we
            can query — surfacing an honest note is safer than silently
            omitting them (PART 3). */}
        <div className="text-[11px] text-slate-400 italic pt-2 border-t" style={{ borderColor: NAVY_HAIRLINE }}>
          Fax and note history: no per-phone endpoint yet — check the client chart for those channels.
        </div>
      </div>

      {/* Iter 488 — inline SMS composer. Reply without switching tabs.
          Enter sends; Shift+Enter adds a newline. Auto-grows up to
          ~5 lines then scrolls internally.
          Iter 494 — paperclip file picker + emoji quick-picker + inline
          attachment chip preview. Removes the biggest gaps operators
          hit vs the old ReceptionistSMSPanel composer. */}
      <div
        className="shrink-0 border-t bg-white p-4 md:p-5"
        style={{ borderColor: NAVY_HAIRLINE }}
        data-testid="comms2-timeline-composer"
      >
        {attachment && (
          <div
            className="flex items-center gap-2 p-2 rounded-lg bg-slate-50 border border-slate-200 mb-2 text-[12px]"
            data-testid="comms2-timeline-attachment-preview"
          >
            <Paperclip className="w-4 h-4 text-slate-500 shrink-0" />
            <span className="truncate flex-1" title={attachment.name}>{attachment.name}</span>
            <span className="text-slate-400 shrink-0">
              {attachment.size < 1024
                ? `${attachment.size} B`
                : attachment.size < 1024 * 1024
                  ? `${(attachment.size / 1024).toFixed(1)} KB`
                  : `${(attachment.size / 1024 / 1024).toFixed(1)} MB`}
            </span>
            <button
              onClick={() => { setAttachment(null); if (fileInputRef.current) fileInputRef.current.value = ""; }}
              className="text-slate-400 hover:text-red-500 shrink-0"
              title="Remove attachment"
              data-testid="comms2-timeline-attachment-remove"
            >
              <XIcon className="w-4 h-4" />
            </button>
          </div>
        )}
        <div className="flex items-end gap-2">
          <input
            type="file"
            ref={fileInputRef}
            className="hidden"
            accept="image/*,video/mp4,video/quicktime,audio/*,.pdf,.doc,.docx,.txt,.csv,.xls,.xlsx"
            onChange={onPickFile}
            data-testid="comms2-timeline-file-input"
          />
          <button
            type="button"
            onClick={() => fileInputRef.current?.click()}
            disabled={sending || uploading}
            className="h-12 w-12 rounded-lg border hover:bg-slate-50 flex items-center justify-center text-slate-500 hover:text-[#143875] transition-colors disabled:opacity-40 shrink-0"
            style={{ borderColor: NAVY_HAIRLINE }}
            title="Attach a file (max 5MB)"
            data-testid="comms2-timeline-attach-btn"
          >
            <Paperclip className="w-5 h-5" />
          </button>
          <div
            className="h-12 w-12 rounded-lg border flex items-center justify-center shrink-0"
            style={{ borderColor: NAVY_HAIRLINE }}
          >
            <EmojiQuickPicker onPick={onEmojiPick} testId="comms2-timeline-emoji" />
          </div>
          <textarea
            ref={textareaRef}
            value={draft}
            onChange={(e) => setDraft(e.target.value.slice(0, SMS_MAX_CHARS))}
            onKeyDown={(e) => {
              if (e.key === "Enter" && !e.shiftKey) {
                e.preventDefault();
                sendReply();
              }
            }}
            placeholder={`Reply to ${entry.name || entry.phone}…  (Enter to send, Shift+Enter for a new line)`}
            rows={4}
            className="flex-1 resize-none rounded-lg border px-4 py-3 text-[16px] leading-relaxed focus:outline-none focus:ring-2 focus:ring-[#143875]/40 min-h-[120px] max-h-[280px]"
            style={{ borderColor: NAVY_HAIRLINE, color: NAVY }}
            data-testid="comms2-timeline-composer-input"
            disabled={sending}
          />
          <button
            onClick={sendReply}
            disabled={sending || uploading || (!draft.trim() && !attachment)}
            className="h-12 px-6 rounded-lg text-white text-[15px] font-bold flex items-center gap-2 disabled:opacity-40 disabled:cursor-not-allowed transition shrink-0"
            style={{ background: NAVY }}
            data-testid="comms2-timeline-composer-send"
            title="Send (Enter)"
          >
            <SendIcon className="w-4 h-4" />
            {uploading ? "Uploading…" : sending ? "Sending…" : "Send"}
          </button>
        </div>
        {draft.length > SMS_SEGMENT && (
          <div className="px-4 pb-2 text-[12px] font-semibold" style={{ color: NAVY }} data-testid="comms2-sms-segment-hint">
            {draft.length} characters · will send as {smsSegments(draft)} text messages (each billed separately)
          </div>
        )}
        <div className="flex items-center justify-between mt-2 text-[12px] text-slate-500">
          <span>
            From: {entry.smsThread?.our_number || entry.ourNumber || fromNumber || <span className="italic text-amber-600">no line selected</span>}
          </span>
          <span data-testid="comms2-timeline-composer-count" className={draft.length > 160 ? "text-amber-600 font-medium" : ""}>
            {draft.length}/160{draft.length > 160 ? ` · ${Math.ceil(draft.length / 153)} segments` : ""}
          </span>
        </div>
      </div>
    </div>
  );
}

/* ═════════════════ Main Inbox Tab ═════════════════ */
export default function InboxTab({
  inboxItems,
  loading,
  filter,
  setFilter,
  onOpenTimeline,
  openEntryKey,
  setOpenEntryKey,
  onCall,
  onText,
  onPlayVm,
  onMarkHandled,
  onHide,
  onDelete,      // iter 484 — delete SMS thread
  onNewText,
  navigate,
  lockFilter = false,   // iter 484 — dedicated Texts tab locks filter to "texts"
  fromNumber,           // iter 488 — outbound line for inline SMS composer
  onInboxChanged,       // iter 488 — bump inbox list after inline send
  shellMode = false,    // Ring Center — force the narrow rail + thread 3-pane layout always
  autoOpenFirst = false,// Ring Center — open the freshest conversation on mount
}) {
  const [pageSize, setPageSize] = useState(PAGE);
  const { user } = useAuth();
  // Iter 508k · click-to-open contract — hoist the app-wide slideover
  // handle here so we can pass it into TimelinePanel for the header
  // name → contact detail affordance.
  const { open: openContactSlideover } = useContactSlideover();
  // Only super_admin sees the tenant badge — every other role is
  // already scoped to their own company, so the badge would be a
  // constant noise repeat.
  const showCompanyBadge = user?.role === "super_admin";

  // Iter 497 — Quick-add contact modal for unmatched inbound numbers
  // shown right from the thread list, so operators name a caller in
  // 2 clicks instead of jumping to /contacts.
  //
  // `addContactCtx` carries { phone, contactId, defaultName } so the
  // modal knows whether to POST a new contact or PATCH an existing
  // placeholder (auto-imported by SMS with no name) — otherwise
  // clicking Add on a nameless-but-existing row would create a
  // duplicate row for the same phone.
  const [addContactCtx, setAddContactCtx] = useState(null);
  const openAddContact = (entry) => {
    if (!entry?.phone) return;
    const c = entry.contact || null;
    const defaultName = c
      ? `${c.first_name || ""} ${c.last_name || ""}`.trim()
      : "";
    setAddContactCtx({
      phone: entry.phone,
      contactId: c?.id || null,
      defaultName,
    });
  };

  // Iter 505 — jump straight into the full edit dialog on /contacts for a
  // named contact. Keeps the light "Add" modal for the unnamed case and
  // hands off the heavy edit (phone, fax, email, address, tags…) to the
  // page that owns the full form.
  const openEditContact = (contactId) => {
    if (!contactId) return;
    // /contacts redirects to /phone via LegacyOrRedirect — ?legacy=1
    // keeps us on the actual /contacts page (PhoneBookPanel), which
    // reads ?edit=<id> on mount and opens the full edit dialog.
    window.location.assign(`/contacts?legacy=1&edit=${encodeURIComponent(contactId)}`);
  };

  // Iter 485 — search + view mode (card vs list). Both live in the
  // URL so they survive refreshes and can be bookmarked.
  //   ?q=alex        → filters items
  //   ?view=cards    → grid layout; default (or ?view=list) is a
  //                    single-row-per-thread list (denser, per user
  //                    feedback that cards took too much space).
  const [params, setParams] = useSearchParams();
  const q = (params.get("q") || "").trim();
  const view = params.get("view") === "cards" ? "cards" : "list";

  const setQ = (val) => setParams(p => {
    const n = new URLSearchParams(p);
    if (val) n.set("q", val); else n.delete("q");
    return n;
  }, { replace: true });
  const setView = (val) => setParams(p => {
    const n = new URLSearchParams(p);
    if (val === "cards") n.set("view", "cards"); else n.delete("view");
    return n;
  }, { replace: true });

  // Filter set based on chip. Hidden items excluded from every view
  // (they'd otherwise leak into the "all" and per-channel views).
  //
  // Iter 508j — added `missed` filter (missed inbound calls not yet
  // marked handled). Retains legacy `calls` / `texts` filters for
  // legacy deep-linked URLs since the redirect is best-effort.
  const visible = useMemo(() => {
    const src = inboxItems.filter(e => !e.hidden);
    let out = src;
    if (filter === "needs") out = src.filter(e => e.needsCallback || e.unheardVm || e.unreadSms);
    else if (filter === "missed") out = src.filter(e => e.calls.some(c => c.outcome === "missed" && !c.handled));
    else if (filter === "calls") out = src.filter(e => e.calls.length > 0);
    else if (filter === "texts") out = src.filter(e => !!e.smsThread);
    else if (filter === "vm")    out = src.filter(e => e.voicemails.length > 0);
    if (q) {
      const needle = q.toLowerCase();
      out = out.filter(e => (
        (e.displayName || "").toLowerCase().includes(needle)
        || (e.phone || "").toLowerCase().includes(needle)
        || (e.latestSummary || "").toLowerCase().includes(needle)
        || (e.contactType || "").toLowerCase().includes(needle)
        || (e.companyName || "").toLowerCase().includes(needle)
      ));
    }
    return out;
  }, [inboxItems, filter, q]);

  const paged = visible.slice(0, pageSize);
  const grouped = useMemo(() => {
    const m = new Map();
    for (const e of paged) {
      const day = dayLabel(e.latestTs);
      if (!m.has(day)) m.set(day, []);
      m.get(day).push(e);
    }
    return Array.from(m.entries());
  }, [paged]);

  // Iter 489 — accept BOTH the full "last10::company_id" key from
  // useInboxRollup AND a bare last-10 (used when opening a contact's
  // Timeline where the caller doesn't know which tenant the row lives
  // under). Bare-key lookups prefer the entry with the most recent
  // activity so we land on the freshest thread. If no entry matches,
  // synthesize a minimal one so the composer can still fire — this is
  // the "brand new text to a contact" case.
  const _matchByBareKey = (bare) =>
    [...inboxItems]
      .filter(e => (e.key || "").split("::")[0] === String(bare))
      .sort((a, b) => (b.latestTs || 0) - (a.latestTs || 0))[0];

  const openEntry = openEntryKey
    ? (inboxItems.find(e => e.key === openEntryKey)
        || _matchByBareKey(openEntryKey)
        || {
            key: openEntryKey,
            phone: String(openEntryKey || "").length === 10 ? `+1${openEntryKey}` : String(openEntryKey || ""),
            ourNumber: "",
            calls: [], voicemails: [], smsThread: null,
            displayName: "", contactType: "", companyId: "", companyName: "",
            contact: null, synthetic: true,
          })
    : null;
  const unnamedCount = inboxItems.filter(e => e.needsRename && !e.hidden).length;

  // Ring Center — open the freshest conversation once on mount so the shell
  // renders as the designed 3-pane (rail + thread + context) instead of an
  // empty full-width list. Guarded so closing a thread doesn't re-open it.
  const didAutoOpen = useRef(false);
  useEffect(() => {
    if (autoOpenFirst && !openEntryKey && !didAutoOpen.current && visible.length) {
      didAutoOpen.current = true;
      setOpenEntryKey(visible[0].key);
    }
  }, [autoOpenFirst, openEntryKey, visible, setOpenEntryKey]);

  const twoPane = !!openEntry || shellMode;
  return (
    <div className="flex-1 flex overflow-hidden bg-slate-50 relative">
      {/* LIST COLUMN — full width normally; narrows to a conversation
          list beside the thread pane on desktop when a thread is open. */}
      <div
        className={cn(
          "flex flex-col overflow-hidden min-w-0",
          twoPane ? "w-full lg:w-[380px] lg:shrink-0 lg:border-r" : "flex-1"
        )}
        style={twoPane ? { borderColor: NAVY_HAIRLINE } : undefined}
      >
      {/* Header — two tidy rows so nothing ever overlaps, even when the
          list is squeezed to a narrow rail beside an open thread:
            Row 1 · filter chips (horizontal-scroll in the narrow rail)
            Row 2 · search (flexes) + view toggle + New text          */}
      <div
        className="shrink-0 flex flex-col gap-2 px-3 sm:px-4 py-2.5 border-b bg-white"
        style={{ borderColor: NAVY_HAIRLINE }}
      >
        {lockFilter ? (() => {
          // Iter 506 · per-filter label + icon for the locked-filter header.
          const filterMeta = {
            needs: { label: "Needs a callback", Icon: PhoneMissed },
            calls: { label: "Call log",         Icon: Phone },
            texts: { label: "Text conversations", Icon: MessageSquare },
            vm:    { label: "Voicemail",        Icon: VMIcon },
          }[filter] || { label: "Activity", Icon: MessageSquare };
          const { label, Icon } = filterMeta;
          return (
            <div className="flex items-center gap-2 text-[13px] font-semibold shrink-0" style={{ color: NAVY }}>
              <Icon className="w-4 h-4" />
              {label}
            </div>
          );
        })() : (
          <FilterChips filter={filter} setFilter={setFilter} scroll={twoPane} />
        )}

        <div className="flex items-center gap-2">
          {/* Iter 485 — searchbox: filters displayName / phone / latest
              message / contact type / company; kept in the URL (`?q=`). */}
          <div className="relative flex-1 min-w-0">
            <Search className="w-3.5 h-3.5 absolute left-2.5 top-1/2 -translate-y-1/2 pointer-events-none" style={{ color: "#64748B" }} />
            <input
              type="search"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search name, number, message…"
              className="w-full h-9 pl-8 pr-8 rounded-md border text-[13px] font-medium focus:outline-none focus:ring-2"
              style={{ borderColor: NAVY_HAIRLINE, color: NAVY }}
              data-testid="comms2-search"
            />
            {q && (
              <button
                onClick={() => setQ("")}
                className="absolute right-1 top-1/2 -translate-y-1/2 h-6 w-6 rounded hover:bg-slate-100 flex items-center justify-center"
                title="Clear search"
                data-testid="comms2-search-clear"
              >
                <XIcon className="w-3 h-3" style={{ color: "#64748B" }} />
              </button>
            )}
          </div>

          {/* View toggle — hidden in two-pane mode (list forced to a single
              column) and on very small screens to keep the row uncramped. */}
          {!twoPane && (
            <div className="hidden sm:flex items-center rounded-md border p-0.5 shrink-0" style={{ borderColor: NAVY_HAIRLINE }}>
              <button
                onClick={() => setView("cards")}
                className="h-8 px-2.5 rounded flex items-center gap-1 text-[12px] font-semibold transition"
                style={{ background: view === "cards" ? NAVY : "transparent", color: view === "cards" ? "white" : NAVY }}
                title="Card view"
                data-testid="comms2-view-cards"
              >
                <LayoutGrid className="w-3.5 h-3.5" /> Cards
              </button>
              <button
                onClick={() => setView("list")}
                className="h-8 px-2.5 rounded flex items-center gap-1 text-[12px] font-semibold transition"
                style={{ background: view === "list" ? NAVY : "transparent", color: view === "list" ? "white" : NAVY }}
                title="List view"
                data-testid="comms2-view-list"
              >
                <List className="w-3.5 h-3.5" /> List
              </button>
            </div>
          )}

          <button
            onClick={onNewText}
            className="h-9 px-3.5 rounded-lg text-[13px] font-bold hover:brightness-95 active:scale-[0.99] transition flex items-center gap-1.5 shrink-0 whitespace-nowrap"
            style={{ background: GOLD, color: NAVY }}
            data-testid="comms2-new-text"
          >
            <MessageSquare className="w-4 h-4" /> New text
          </button>
        </div>
      </div>

      {/* Scrolling body */}
      <div className="flex-1 overflow-y-auto p-4">
        {!twoPane && (
          <TriageStrip
            unnamedCount={unnamedCount}
            onReview={() => navigate("/phone?tab=contacts&filter=unnamed")}
            onHideAll={() => {
              // Bulk-hide every unnamed number that appears in the inbox
              for (const e of inboxItems) if (e.needsRename) onHide(e.key);
            }}
          />
        )}

        {loading && visible.length === 0 && (
          <div className="text-center text-slate-400 py-8 text-[13px]">Loading…</div>
        )}
        {!loading && visible.length === 0 && (() => {
          // Context-aware empty state — the previous hardcoded "Nothing
          // needs a response" copy was confusing on the Texts / Calls /
          // VM tabs and after a search that returned zero.
          const filterLabel = {
            needs: "Nothing needs a response",
            calls: "No calls to show",
            texts: "No text conversations",
            vm:    "No voicemails",
          }[filter] || "Nothing to show";
          const searchSuffix = q ? ` matching "${q}"` : "";
          return (
            <div className="text-center text-slate-400 py-12" data-testid="comms2-empty-state">
              <div className="text-[14px] font-medium mb-1" style={{ color: NAVY }}>
                {filterLabel}{searchSuffix}
              </div>
              <div className="text-[12px]">
                {q
                  ? "Try clearing the search or switching filter tabs."
                  : filter === "needs"
                    ? "You're caught up."
                    : "New activity will show up here."}
              </div>
              {q && (
                <button
                  onClick={() => setQ("")}
                  className="mt-3 text-[12px] font-semibold underline"
                  style={{ color: NAVY }}
                  data-testid="comms2-empty-clear-search"
                >
                  Clear search
                </button>
              )}
            </div>
          );
        })()}
        {grouped.map(([day, rows]) => (
          <div key={day} className="mb-5">
            <div className="text-[11px] uppercase tracking-widest font-semibold text-slate-500 mb-2">{day}</div>
            {(view === "list" || twoPane) ? (
              <div className="rounded-lg border overflow-hidden bg-white" style={{ borderColor: NAVY_HAIRLINE }}>
                {rows.map((e, idx) => (
                  <InboxRow
                    key={e.key}
                    entry={e}
                    onOpen={(x) => { setOpenEntryKey(x.key); onOpenTimeline && onOpenTimeline(x); }}
                    onCall={onCall}
                    onText={onText}
                    onPlayVm={onPlayVm}
                    onMarkHandled={onMarkHandled}
                    onHide={onHide}
                    onDelete={onDelete}
                    onAddContact={openAddContact}
                    onEditContact={openEditContact}
                    showCompanyBadge={showCompanyBadge}
                    isLast={idx === rows.length - 1}
                  />
                ))}
              </div>
            ) : (
              <div className={cn("grid gap-3", twoPane ? "grid-cols-1" : "grid-cols-1 md:grid-cols-2 xl:grid-cols-3")}>
                {rows.map(e => (
                  <InboxCard
                    key={e.key}
                    entry={e}
                    onOpen={(x) => { setOpenEntryKey(x.key); onOpenTimeline && onOpenTimeline(x); }}
                    onCall={onCall}
                    onText={onText}
                    onPlayVm={onPlayVm}
                    onMarkHandled={onMarkHandled}
                    onHide={onHide}
                    onDelete={onDelete}
                    onAddContact={openAddContact}
                    onEditContact={openEditContact}
                    showCompanyBadge={showCompanyBadge}
                  />
                ))}
              </div>
            )}
          </div>
        ))}
        {/* Pagination — spec explicitly forbids infinite scroll */}
        {visible.length > paged.length && (
          <div className="text-center pt-3">
            <button
              onClick={() => setPageSize(sz => sz + PAGE)}
              className="h-9 px-4 rounded-md border text-[12.5px] font-medium hover:bg-white"
              style={{ borderColor: NAVY_HAIRLINE, color: NAVY }}
              data-testid="comms2-load-more"
            >
              Load {Math.min(PAGE, visible.length - paged.length)} more · showing {paged.length} of {visible.length}
            </button>
          </div>
        )}
      </div>
      </div>

      {openEntry && (
        <div
          className="bg-white flex min-w-0 absolute inset-0 z-[60] shadow-2xl lg:static lg:z-auto lg:flex-1 lg:shadow-none"
          data-testid="comms2-thread-pane"
        >
          <TimelinePanel
            entry={openEntry}
            onClose={() => setOpenEntryKey(null)}
            onCall={onCall}
            navigate={navigate}
            fromNumber={fromNumber}
            onInboxChanged={onInboxChanged}
            openContactSlideover={openContactSlideover}
          />
        </div>
      )}

      {/* Ring Center — empty-thread placeholder so the center pane never
          shows as a blank white void when no conversation is open. */}
      {shellMode && !openEntry && (
        <div className="hidden lg:flex flex-1 min-w-0 items-center justify-center bg-slate-50" data-testid="comms2-thread-empty">
          <div className="text-center px-6">
            <MessageSquare className="w-9 h-9 mx-auto mb-3" style={{ color: "#94A3B8" }} />
            <div className="text-[15px] font-semibold" style={{ color: NAVY }}>Select a conversation</div>
            <div className="text-[12.5px] text-slate-500 mt-1">Pick a thread from the list to view messages and reply.</div>
          </div>
        </div>
      )}

      {/* Iter 497 — Quick-add contact for unmatched inbound numbers.
          Refreshes the inbox on save so the entry rehydrates with the
          new displayName + contactType. Iter 498 · follow-up — pass
          contactId + defaultName so nameless placeholder contacts get
          PATCHed instead of duplicated. */}
      <QuickAddContactModal
        open={!!addContactCtx}
        phone={addContactCtx?.phone || ""}
        contactId={addContactCtx?.contactId || null}
        defaultName={addContactCtx?.defaultName || ""}
        onClose={(saved) => {
          setAddContactCtx(null);
          if (saved) onInboxChanged?.();
        }}
      />
    </div>
  );
}
